import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Link } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardPageFormBody,
  buildDashboardDataTableColumns,
  useDashboardDataTableClientPage,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import { ShigoPermission } from '../../../../../auth/permissions';
import { useShigoPermissions } from '../../../../../auth/useShigoPermissions';
import { manageDialogFetchSize } from '../../../../../../lib/clientSelectionList';
import { useTargetingBuilderDeviceTypesQuery } from '../../../../../targeting/builder/hooks';
import { createTargetingSummaryColumns } from '../../../../../targeting/builder/components/TargetingSummaryColumns';
import { usePlacementTargetingsQuery } from '../../../hooks';
import type { PlacementLookupFilter, PlacementTargeting } from '../../../model';
import type { PlacementFormValue } from '../../../placementFormSchema';
import { PlacementSelectionDialog } from '../../PlacementSelectionDialog';

const lookupFilter: PlacementLookupFilter = {
  searchTerm: '',
  page: 0,
  size: manageDialogFetchSize,
};

export function PlacementTargetingFields() {
  'use no memo';
  const { control, setValue } = useFormContext<PlacementFormValue>();
  const { can } = useShigoPermissions();
  const canEditTargeting = can(ShigoPermission.TargetingEdit);
  const targetings = useWatch({ control, name: 'targetings' });
  const [manageOpen, setManageOpen] = React.useState(false);
  const query = usePlacementTargetingsQuery(lookupFilter, manageOpen);
  const deviceTypesQuery = useTargetingBuilderDeviceTypesQuery();
  const deviceTypeNames = new Map(
    (deviceTypesQuery.data ?? []).map((deviceType) => [deviceType.id, deviceType.name]),
  );
  const summaryColumns = createTargetingSummaryColumns<PlacementTargeting>(deviceTypeNames);
  const columns = buildDashboardDataTableColumns<PlacementTargeting>([
    {
      id: 'name',
      header: dashboardT('placements.name'),
      kind: DashboardDataTableColumnKind.Text,
      value: (item) =>
        canEditTargeting && item.id ? (
          <Link
            className={dashboardTableTextActionClassName()}
            title={item.name}
            to="/targeting/$targetingId/edit"
            params={{ targetingId: item.id }}
          >
            {item.name}
          </Link>
        ) : (
          item.name
        ),
      headerClassName: 'min-w-[280px]',
      className: 'min-w-[280px]',
    },
    ...summaryColumns,
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      cell: (item) => (
        <DashboardTableRowActions label={dashboardT('placements.actionsFor', { name: item.name })}>
          <DashboardTableRowActionItem
            onSelect={() => {
              setValue(
                'targetings',
                targetings.filter((targeting) => targeting.id !== item.id),
                { shouldDirty: true },
              );
            }}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {dashboardT('placements.removeTargeting')}
          </DashboardTableRowActionItem>
        </DashboardTableRowActions>
      ),
    },
  ]);
  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(targetings);
  return (
    <DashboardPageFormBody width="lg">
      <DashboardDataTable
        title={dashboardT('placements.targeting')}
        tableLabel={dashboardT('placements.targeting')}
        columns={columns}
        items={tablePage.items}
        pagination={tablePage.pagination}
        getRowKey={(item) => item.id}
        emptyMessage={dashboardT('placements.noSelectedTargeting')}
        minWidthClassName="min-w-[620px]"
        toolbar={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setManageOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {dashboardT('placements.manageTargeting')}
            </Button>
            {targetings.length ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (window.confirm(dashboardT('placements.clearTargetingConfirm')))
                    setValue('targetings', [], { shouldDirty: true });
                }}
              >
                {dashboardT('placements.removeAll')}
              </Button>
            ) : null}
          </>
        }
      />
      <PlacementSelectionDialog
        open={manageOpen}
        title={dashboardT('placements.manageTargeting')}
        items={query.data?.content ?? []}
        selected={targetings}
        loading={query.isLoading || query.isFetching}
        error={query.isError}
        getId={(item) => item.id}
        getName={(item) => item.name}
        columns={summaryColumns}
        onOpenChange={setManageOpen}
        onConfirm={(items) => {
          setValue('targetings', items, { shouldDirty: true });
        }}
      />
    </DashboardPageFormBody>
  );
}
